/**
 * Tax History API service (S1-P6 — read-only tax-form history).
 *
 * Promise-based wrapper around the real backend endpoints under
 * `api/v1/TaxForm` (all created in S1-P4; this slice adds NO new endpoint —
 * it only consumes them). Mirrors the axios/base-URL/cookie-auth +
 * ResponseModel `unwrap`/`toError` pattern of `services/taxQuestionApi.js`.
 * All responses are wrapped in the standard ResponseModel
 * `{ message, status, data }`; helpers below unwrap `.data` and throw on a
 * non-success `status` (APIStatus.Successful === 0) so callers can `await` the
 * inner payload directly and `catch` a friendly Error (the thrown Error carries
 * `.httpStatus`/`.status` so a view can detect a 404).
 *
 * NOTE (JS project — no TypeScript): shapes documented via JSDoc typedefs.
 *
 * @typedef {Object} TaxFormSummaryDto
 * @property {number} formID @property {string} sessionID @property {?string} userID
 * @property {?string} channel @property {?number} yearID @property {?string} taxYearCode
 * @property {?number} taxConfigVersionID @property {?number} questionVersionID @property {string} status
 * @property {?string} currentGroupCode @property {number} answeredCount @property {number} totalRequired
 * @property {number} skippedCount fields the customer explicitly skipped (never counted in answeredCount)
 * @property {boolean} isJointFiling @property {?string} startedAt @property {?string} completedAt
 *
 * @typedef {Object} TaxFormAnswerDto
 * @property {number} answerID @property {number} questionItemID @property {number} personIndex @property {?string} itemKind
 * @property {?string} groupCode @property {?string} groupLabel @property {?string} ruleCode @property {?string} fieldKey
 * @property {?string} questionTextTh @property {?string} rawAnswer @property {?string} normalizedValue
 * @property {?string[]} answerValues @property {boolean} isLlmParsed @property {?string} answeredAt
 * @property {string} answerStatus 'Answered' | 'Skipped' (ลูกค้าขอข้ามไว้ — row exists but has no value)
 * @property {string} valueSource 'User' | 'System' | 'Dopa'
 *
 * @typedef {Object} TaxFormFullDto
 * @property {TaxFormSummaryDto} form @property {TaxFormAnswerDto[]} answers
 *
 * @typedef {Object} TaxFormListResult SearchResult<TaxFormSummaryDto>
 * @property {TaxFormSummaryDto[]} items @property {number} totalCount @property {number} page
 * @property {number} pageNumber @property {number} pageSize @property {number} totalPages
 */

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_APP_API || 'http://localhost:5000/';

// Dedicated instance. `withCredentials` is set explicitly (cookie session auth)
// so it does not depend on the global axios default being applied first.
const http = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

const API_STATUS_SUCCESS = 0; // APIStatus.Successful

/**
 * Unwrap a ResponseModel `{ message, status, data }` → inner `data`.
 * Throws an Error (with `.status`/`.data`) when the envelope reports failure.
 */
function unwrap(response) {
  const body = response && response.data;
  if (body && typeof body === 'object' && 'status' in body) {
    if (body.status === API_STATUS_SUCCESS) {
      return body.data;
    }
    const err = new Error(body.message || 'Request failed');
    err.status = body.status;
    err.data = body.data;
    throw err;
  }
  return body;
}

/** Normalize an axios/transport error into a friendly Error carrying the API message. */
function toError(error) {
  const body = error && error.response && error.response.data;
  const message =
    (body && (body.message || body.Message)) ||
    (error && error.message) ||
    'Request failed';
  const err = new Error(message);
  err.httpStatus = error && error.response && error.response.status;
  err.status = body && (body.status ?? body.Status);
  err.data = body && (body.data ?? body.Data);
  return err;
}

async function get(url) {
  try {
    return unwrap(await http.get(url));
  } catch (e) {
    throw e instanceof Error && e.httpStatus === undefined && e.status !== undefined ? e : toError(e);
  }
}

const ROOT = 'api/v1/TaxForm';

export const taxHistoryApi = {
  /**
   * List tax forms (company-wide; tenant scope applied server-side).
   * @param {{ yearId?:number, status?:string, sessionId?:string, userId?:string, page?:number, pageSize?:number }} [filter]
   * @returns {Promise<TaxFormListResult>}
   */
  list(filter = {}) {
    const p = new URLSearchParams();
    if (filter.yearId != null) p.set('yearId', filter.yearId);
    if (filter.status) p.set('status', filter.status);
    if (filter.sessionId) p.set('sessionId', filter.sessionId);
    if (filter.userId) p.set('userId', filter.userId);
    p.set('page', filter.page ?? 1);
    p.set('pageSize', filter.pageSize ?? 50);
    return get(`${ROOT}?${p.toString()}`);
  },
  /** @returns {Promise<TaxFormFullDto>} form + every captured answer */
  getById(formId) {
    return get(`${ROOT}/${formId}`);
  },
  /** @returns {Promise<TaxFormFullDto>} current form for a session (optional/unused in MVP) */
  getBySession(sessionId) {
    return get(`${ROOT}/session/${encodeURIComponent(sessionId)}`);
  },
};

export default taxHistoryApi;
